// FLOW Platform — REST API client.
//
// Thin fetch() wrapper around flow/v1. Responsibilities:
//
//   - Unwrap the standard success envelope ({ data, meta }) and throw a
//     structured Error on the standard error envelope.
//   - Send the guest token as `Authorization: Bearer <token>` on every
//     authed request. The server middleware also accepts the HttpOnly
//     cookie, but the cookie isn't readable from JS, so the localStorage
//     token + Bearer header is the canonical client path.
//   - Generate an Idempotency-Key on every non-GET request, and retry
//     once on network errors with the same key so a flaky connection
//     doesn't double-submit.
//
// Closed error codes returned by flow/v1:
//   validation_failed, not_found, unauthorized, forbidden, conflict,
//   rate_limited, method_not_allowed, idempotency_mismatch, internal_error
//
// We map any non-envelope failure (network down, JSON parse error,
// non-flow response shape) to a synthetic 'network_error' code so
// callers have a single set of branches to handle.

const NETWORK_ERROR = 'network_error';
const PARSE_ERROR = 'parse_error';

/**
 * Custom error type with the closed-set code surfaced.
 */
export class FlowApiError extends Error {
  constructor(code, message, requestId, details) {
    super(message);
    this.name = 'FlowApiError';
    this.code = code;
    this.requestId = requestId || null;
    this.details = details || [];
  }
}

/**
 * Generate a UUIDv4-ish string for Idempotency-Key headers. Falls back
 * to a timestamp + random suffix on platforms without crypto.randomUUID
 * (only relevant for very old browsers; we target ES2019+).
 */
function newIdempotencyKey() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch (e) {
    // fall through to fallback
  }
  return (
    'fht-' +
    Date.now().toString(16) +
    '-' +
    Math.random().toString(16).slice(2, 10)
  );
}

/**
 * Build an ApiClient bound to a REST base URL.
 *
 * The `getToken()` callback is read each time we send a request so the
 * client picks up token rotations without needing to be re-instantiated.
 *
 *   const client = createApiClient({
 *     restBase: '/wp-json/flow/v1/',
 *     getToken: () => localStorage.getItem('flow_guest_token'),
 *   });
 *
 * Returns an object with .get/.post helpers that return the unwrapped
 * `data` field on success and throw FlowApiError on failure.
 */
export function createApiClient({ restBase, getToken }) {
  // Normalise the base URL so callers can pass with or without trailing /.
  const base = restBase.endsWith('/') ? restBase : restBase + '/';

  async function request(method, path, { body, idempotencyKey } = {}) {
    // Clean any leading slash on `path` so we don't double-slash.
    const url = base + path.replace(/^\//, '');

    const headers = {
      Accept: 'application/json',
    };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    if (method !== 'GET' && idempotencyKey) {
      headers['Idempotency-Key'] = idempotencyKey;
    }

    // Pull the token fresh on every request.
    const token = typeof getToken === 'function' ? getToken() : null;
    if (token) {
      headers.Authorization = 'Bearer ' + token;
    }

    let response;
    try {
      response = await fetch(url, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        // Send cookies so the HttpOnly cookie path also works if for some
        // reason the Bearer header is stripped en route.
        credentials: 'same-origin',
      });
    } catch (networkErr) {
      throw new FlowApiError(
        NETWORK_ERROR,
        'Could not reach the server. Check your connection and try again.',
        null,
        [],
      );
    }

    // Try to parse a JSON body even on non-2xx; flow/v1 always replies
    // with a JSON envelope (success or error).
    let envelope;
    try {
      envelope = await response.json();
    } catch (parseErr) {
      throw new FlowApiError(
        PARSE_ERROR,
        'The server returned an unexpected response.',
        null,
        [],
      );
    }

    if (response.ok) {
      // Success envelope: { data, meta: { request_id } }
      if (envelope && Object.prototype.hasOwnProperty.call(envelope, 'data')) {
        return {
          data: envelope.data,
          meta: envelope.meta || {},
        };
      }
      // Defensive — server should always send `data`.
      throw new FlowApiError(
        PARSE_ERROR,
        'The server returned a response we did not understand.',
        null,
        [],
      );
    }

    // Error envelope: { error: { code, message, details? }, request_id }
    const err = (envelope && envelope.error) || {};
    throw new FlowApiError(
      err.code || 'internal_error',
      err.message || 'Something went wrong.',
      envelope.request_id || null,
      err.details || [],
    );
  }

  /**
   * Send a non-GET request with at most one transparent retry on
   * `network_error`. The Idempotency-Key is preserved across the retry
   * so the server replays the cached response instead of double-handling.
   */
  async function writeWithRetry(method, path, body) {
    const idempotencyKey = newIdempotencyKey();
    try {
      return await request(method, path, { body, idempotencyKey });
    } catch (err) {
      if (err instanceof FlowApiError && err.code === NETWORK_ERROR) {
        // One retry, same key.
        return await request(method, path, { body, idempotencyKey });
      }
      throw err;
    }
  }

  return {
    get(path) {
      return request('GET', path);
    },
    post(path, body) {
      return writeWithRetry('POST', path, body);
    },
  };
}
